"use client";

// Core
import { ReactNode } from "react";
import { SWRConfig, type SWRConfiguration } from "swr";

// Store
import UserStore from "@/store/user-store";

// Utils
import { log } from "@/utils/browser-logger";
import { verifyBuild } from "@/utils/verify-build";

const SWROptions: SWRConfiguration = {
  errorRetryInterval: 30_000,
  errorRetryCount: 5,
  shouldRetryOnError: true,
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  refreshInterval: 0,
  dedupingInterval: 5_000,
  keepPreviousData: false,
  refreshWhenOffline: false,
  refreshWhenHidden: false,

  onError: (error: unknown, key: string) => {
    if (verifyBuild().isDevelopment) {
      const msg =
        typeof error === "object" && error !== null && "message" in error
          ? String((error as { message?: unknown }).message)
          : String(error);

      log.crucial(`Error query key: ${key}.`, `Error global handler: ${msg}`);
    }
  },

  onErrorRetry: (error: any, _key, config, revalidate, opts) => {
    const status = error?.response?.status as number | undefined;
    if (status && status >= 400 && status < 500) return;
    if (opts.retryCount >= (config.errorRetryCount ?? 5)) return;

    const interval = config.errorRetryInterval ?? 30_000;

    setTimeout(() => {
      void revalidate({ retryCount: opts.retryCount + 1 });
    }, interval);
  },
};

export const SWRProvider = ({ children }: { children: ReactNode }) => {
  const hasHydrated = UserStore((s) => s.hasHydrated);

  if (!hasHydrated) return null;

  return <SWRConfig value={SWROptions}>{children}</SWRConfig>;
};
